import Skeleton from "@/components/Skeleton/Skeleton";
import SingleBarChart, {
  SingleBarChartData,
} from "@/components/visualizations/SingleBarChart.tsx/SingleBarChart";
import useVisualization from "@/components/visualizations/useVisualization";
import Widget from "@/pages/dashboard/components/Widget/Widget";
import { useDashboardOrderCounts } from "@/pages/dashboard/hooks/usePoSo";
import {
  ProductBy,
  useProductImpactTotalsPerLifecyclePerProduct,
  useProductMaterialImpactPerMaterial,
} from "@/pages/dashboard/hooks/useProductImpact";
import { formatLifecycleLabel } from "@/pages/dashboard/utils/formatters";
import { ImpactMetric, ImpactMetricGroupIcons } from "@/pages/dashboard/utils/types";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Tooltip } from "antd";
import styles from "./ProductDrawer.module.scss";

function ProductImpactTotals({
  metric,
  productId,
  by,
}: {
  metric: ImpactMetric;
  productId: number;
  by: ProductBy;
}) {
  const { formatLabel } = useVisualization({
    format: metric.formatter,
  });
  const { data, isLoading, isFetching } = useProductImpactTotalsPerLifecyclePerProduct({
    productId,
    by,
    metrics: [metric.key],
  });

  const { data: orderCounts, isLoading: orderCountLoading } = useDashboardOrderCounts({
    productId,
  });
  const { data: materialData } = useProductMaterialImpactPerMaterial({
    productId,
    by,
    metric: metric.key,
  });

  const chartData: SingleBarChartData =
    data && data.impact.length > 0
      ? data.impact.map((row) => ({
          label: formatLifecycleLabel(row.label),
          value: row.impact[metric.key],
        }))
      : [];

  const content = (
    <>
      <Flex
        align={"center"}
        justify={"space-between"}
        className={styles.productImpactTotalsContainer}
      >
        <Flex
          className={styles.item}
          align={"center"}
          gap={12}
          style={{ flexGrow: 0.3 }}
        >
          <Flex vertical>
            <div>
              {isLoading ? (
                <Skeleton style={{ width: "100%", height: 45 }} />
              ) : (
                <Flex
                  align={"center"}
                  justify={"left"}
                  gap={12}
                  className={styles.metric}
                >
                  <div>
                    {metric.icon || ImpactMetricGroupIcons[metric.impactGroup || "Main impacts"]}
                  </div>
                  <span className={styles.metricValue}>
                    {formatLabel(data?.total_impact[metric.key] || 0, true)}
                  </span>
                </Flex>
              )}
            </div>
            <div className={styles.label}>
              <Flex
                align={"center"}
                justify={"left"}
                gap={4}
              >
                <span className={styles.unit}>{metric?.unit}</span>
                <span className={styles.unit}>-</span>
                <span className={styles.metricLabel}>{metric?.label}</span>
                <Tooltip title={metric?.unitTooltip}>
                  <InfoCircleOutlined style={{ color: "#8C8C8C" }} />
                </Tooltip>
              </Flex>
            </div>
          </Flex>
        </Flex>
        <Flex
          className={[styles.item, styles.leftBordered].join(" ")}
          align={"center"}
          gap={12}
        >
          <Flex vertical>
            {isLoading ? (
              <Skeleton style={{ width: 70, height: 45 }} />
            ) : (
              <div className={styles.countValue}>{orderCounts?.purchased_quantity}</div>
            )}
            <div className={styles.label}>Purchased</div>
          </Flex>
        </Flex>
        <Flex
          className={[styles.item, styles.leftBordered].join(" ")}
          align={"center"}
          gap={12}
        >
          <Flex vertical>
            {isLoading ? (
              <Skeleton style={{ width: 70, height: 45 }} />
            ) : (
              <div className={styles.countValue}>{orderCounts?.sold_quantity}</div>
            )}
            <div className={styles.label}>Sold</div>
          </Flex>
        </Flex>

        <Flex
          className={[styles.item, styles.leftBordered].join(" ")}
          align={"center"}
          gap={12}
        >
          <Flex vertical>
            {isLoading ? (
              <Skeleton style={{ width: 70, height: 45 }} />
            ) : (
              <div className={styles.countValue}>{materialData?.total_count}</div>
            )}
            <div className={styles.label}>Materials</div>
          </Flex>
        </Flex>
      </Flex>
      {(!!orderCounts?.po || !!orderCounts?.so || orderCountLoading) && (
        <Flex
          justify={"center"}
          className={styles.chartContainer}
        >
          <div className={styles.chart}>
            <SingleBarChart
              colorPalette="forest_green"
              data={chartData}
              format={metric.formatter}
              loading={isLoading || isFetching}
            />
          </div>
        </Flex>
      )}
    </>
  );

  return <Widget children={content} />;
}

export default ProductImpactTotals;
